import { API, AppConfig } from "api";
import {
    AdMaterial,
    AdMaterialDetails,
    CreativeUploads,
    MaterialSpecification,
    MaterialUpload,
    Permission,
    PreflightReport,
    PrintPreflightReport,
    Status,
} from "api/creatives";

export interface CreativesUploadViewModel {
    currentMaterialSpecification: number;
    materials: MaterialDetailsViewModel[];
}

export interface CreativesViewModel {
    config: AppConfig;
    materialSpecifications: MaterialSpecificationViewModel[];
    adDetails: AdDetailsViewModel;
    user: string;
}

export interface MaterialSpecificationViewModel {
    // eslint-disable-next-line
    [key: string]: any;
    id: number;
    name: string;
    isPrinted: boolean;
    width: string;
    height: string;
    size: string;
    isUploadingOptional: boolean;
    materials: MaterialDetailsViewModel[];
    caption: string;
    firstInsertionDate: string;
    status: Status;
    type: {
        name: string;
        titles: string[];
    };
    permissions: Permission[];
    isStatusUpdateProcessing: boolean; // flag checking matspec deliver/approve action
    isWorking: boolean; // flag checking matspec work progress
    customerNote?: string;
    creativeType: string;
    adServerUrl?: string;
}

export interface MaterialDetailsViewModel {
    id: number;
    version?: number;
    bookedSize: string;
    inputFileName: string;
    externalId: string;
    actions: { [key: string]: string };
    archived: boolean;
    assigned: boolean;
    creationDate: string;
    deleted: boolean;
    description: string;
    format: string;
    hasErrors: boolean;
    modificationDate: string;
    originalMaterialInfo?: AdMaterialDetailsViewModel[];
    materialBigPreview?: AdMaterialDetailsViewModel;
    materialPagesPreview?: MaterialPagesPreview;
    materialInfoPreview?: AdMaterialDetailsViewModel;
    status: Status;
    dimensions: string;
    totalFileSize: string;
    processedHeight: number;
    processedWidth: number;
    sgCode: number;
    pixelTracker?: string;
    tag?: string;
    url: string;
    urls: string[];
    permissions: Permission[];
    preflightReport: PreflightReport | null; // composed property
    printPreflightReport: PrintPreflightReport | null; // composed property
    isUploading: boolean; // composed
    isDeleting: boolean; // composed
    isThirdPartyTagMaterial: boolean; //composed
}

export interface Page {
    url: string;
}
export interface MaterialPagesPreview {
    selectedPage: number;
    pages: Page[];
}

export interface AdMaterialDetailsViewModel {
    format: string;
    height: string;
    id: number;
    serverFileName: string;
    size: string;
    type: string;
    url: string;
    width: string;
}

export interface AdDetailsViewModel {
    // eslint-disable-next-line
    [key: string]: any;
    id: number;
    orderNo: string;
}

const createPreviewDetails = (details?: AdMaterialDetails) =>
    details
        ? {
              id: details.id,
              format: details.format,
              height: details.height,
              size: details.size,
              type: details.type,
              url: details.url,
              width: details.width,
              serverFileName: details.serverfilename,
          }
        : undefined;

/**
 * Creates materials view model
 * @param material
 */
export const buildMaterials = (material: AdMaterial[]): MaterialDetailsViewModel[] => {
    return material.map(({ preview, bigpreview, original, previewsArray, ...material }) => {
        return {
            id: material.id,
            version: material.version,
            inputFileName: material.inputfilename,
            externalId: material.externalid,
            actions: material.actions,
            archived: material.archived,
            assigned: material.assigned,
            deleted: material.deleted,
            description: material.description || "",
            format: material.format,
            dimensions: Array.isArray(material.size) ? "" : material.size,
            bookedSize: material.bookedsizes,
            status: material.status,
            creationDate: material.creationdate,
            hasErrors: material.haserrors?.length !== 0 || false,
            modificationDate: material.creationdate,
            totalFileSize: material.totalfilesize,
            processedWidth: material.processedwidth,
            processedHeight: material.processedheight,
            sgCode: material.sgcode,
            tag: material.tag,
            pixelTracker: material.pixeltracker,
            url: material.url,
            urls: material.urls,
            permissions: material.permissions,
            originalMaterialInfo: !Array.isArray(original)
                ? []
                : original?.map((original) => {
                      return {
                          id: original.id,
                          format: original.format,
                          height: original.height,
                          size: original.size,
                          type: original.type,
                          url: original.url,
                          width: original.width,
                          serverFileName: original.serverfilename,
                      };
                  }),
            materialInfoPreview: createPreviewDetails(preview),
            materialBigPreview: createPreviewDetails(bigpreview),
            materialPagesPreview:
                previewsArray && previewsArray.length > 0
                    ? {
                          selectedPage: 0,
                          pages: previewsArray.map(({ url }) => ({
                              url: `${API.serverUrl}${url}`,
                          })),
                      }
                    : undefined,
            preflightReport: null,
            printPreflightReport: null,
            isUploading: false,
            isDeleting: false,
            isThirdPartyTagMaterial: material.inputfilename.startsWith("thirdparty"), // file name always starts with thirdpartytag if it was created by it
        };
    });
};

export const buildMaterialSpecs = (materialSpecs: MaterialSpecification[]) =>
    materialSpecs.map(
        ({
            admaterial,
            id,
            name,
            printed,
            height,
            width,
            size,
            caption,
            type,
            firstinsertiondate,
            status,
            permissions,
            customernote,
            creativetype,
            adserverurl,
        }) => {
            return {
                id,
                name,
                width,
                height,
                size,
                isPrinted: printed,
                materials: buildMaterials(admaterial),
                isUploadingOptional: false,
                caption,
                status,
                type: {
                    name: type.name,
                    titles: type.titles,
                },
                firstInsertionDate: firstinsertiondate,
                permissions,
                isStatusUpdateProcessing: false,
                isWorking: false,
                customerNote: customernote,
                creativeType: creativetype,
                adServerUrl: adserverurl,
            };
        }
    );

export const buildCreativesViewModel = ({ matspecs, ad, user, config }: CreativeUploads): CreativesViewModel => {
    return {
        config,
        materialSpecifications: buildMaterialSpecs(matspecs),
        adDetails: {
            id: ad.id,
            orderNo: ad.orderno,
        },
        user,
    };
};

export const buildUploadedMaterialsViewModel = (materials: MaterialUpload[]): CreativesUploadViewModel => {
    // can contain multiple materials but we can take only first one to get it's id due to duplicated properties
    const [material] = materials;

    return {
        currentMaterialSpecification: material.matspecid,
        materials: materials.reduce<MaterialDetailsViewModel[]>((materialReducer, currentMaterial) => {
            if (materialReducer.length === 0) {
                return buildMaterials([currentMaterial.admaterial]);
            }

            return [...materialReducer, ...buildMaterials([currentMaterial.admaterial])];
        }, []),
    };
};

/**
 * Replaces existing materials with new ones or create new if it does not exist for particular material spec id
 * @param currentMaterialSpecification
 * @param materialSpecifications
 * @param materials
 */
export const createFilteredMaterialSpecs = (
    currentMaterialSpecification: number,
    materialSpecifications: MaterialSpecificationViewModel[],
    materials: MaterialDetailsViewModel[]
) => {
    return materialSpecifications.map((materialSpecification) => {
        if (currentMaterialSpecification === materialSpecification.id) {
            // only one print material can exist
            if (materialSpecification.isPrinted) {
                return {
                    ...materialSpecification,
                    isUploadingOptional: false,
                    materials: materials.map((material) => {
                        return {
                            ...material,
                            isUploading: false,
                            isDeleting: false,
                        };
                    }),
                };
            }

            const existingMaterialIds = new Set(materialSpecification.materials.map(({ id }) => id));
            const addedMaterial = materials.filter(({ id }) => !existingMaterialIds.has(id));

            if (addedMaterial.length) {
                return {
                    ...materialSpecification,
                    isUploadingOptional: false,
                    materials: [
                        ...materialSpecification.materials.map((material) => {
                            return {
                                ...material,
                                isUploading: false,
                                isDeleting: false,
                            };
                        }),
                        ...materials.filter(({ id }) => !existingMaterialIds.has(id)),
                    ],
                };
            }

            return {
                ...materialSpecification,
                isUploadingOptional: false,
                materials: materialSpecification.materials.map((material) => {
                    const replacedMaterial = materials.find(({ id }) => material.id === id);

                    if (replacedMaterial) {
                        return {
                            ...replacedMaterial,
                            isUploading: false,
                            isDeleting: false,
                        };
                    }

                    return material;
                }),
            };
        }

        return materialSpecification;
    });
};
